/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useCallback, useState } from 'react';
import { Button, InputGroup, Tag } from '@blueprintjs/core';
import { useFacetsState } from 'hooks/useFacetsState';
import debounce from 'lodash/debounce';
import { alignedMinimalButton } from 'styles/styledComponentUtils';

/**
 * Search is a parameter generator for facets/hierarchical facets
 * When the user clicks the button or presses 'Enter',
 * the optional textFilter parameter on GenerateFacets is set to the current input text
 *
 * @param {string} findIn what is this search bar looking in?
 * @returns {*}
 * @constructor
 */
export const FacetSearch = ({ findIn = 'filters', ...props }) => {
	const { getTextFilter, updateTextFilter } = useFacetsState();
	const textFilter = getTextFilter();
	const [text, setText] = useState(textFilter);
	const [showInput, setShowInput] = useState(false);

	// reset the local text in the case that another component updates the textFilter
	// useEffect(() => {
	// 	setText(textFilter);
	// }, [textFilter]);

	// update the text filter state
	const debouncedUpdateFilter = useCallback(
		debounce((text) => {
			updateTextFilter(text);
		}, 500),
		[updateTextFilter]
	);

	// clear input on blur
	const handleBlur = (event) => {
		if (!event.currentTarget.contains(event.relatedTarget)) {
			setShowInput(false);
			setText(textFilter);
		}
	};

	// Clear input
	const handleClearText = (e) => {
		e.stopPropagation(); // for clicking on the tag "X" icon
		setShowInput(false);
		setText('');
		updateTextFilter('');
	};

	// On enter
	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault(); // Prevent form submit
			setShowInput(false);
			updateTextFilter(text);
		},
		[text, updateTextFilter]
	);

	const handleOnChange = (e) => {
		const text = e.target.value;
		setText(text);
		debouncedUpdateFilter(text);
	};

	const handleShowInput = () => {
		setText(textFilter); // do this so local text is always up-to-date with global textFilter
		setShowInput(true);
	};
	return (
		<form {...props} onSubmit={handleSubmit} onBlur={handleBlur}>
			{showInput ? (
				<InputGroup
					cy-test="search-filters"
					onChange={handleOnChange}
					placeholder={`Find in ${findIn}...`}
					value={text}
					css={inputStyle}
					fill
					rightElement={<Button icon={'cross'} onClick={handleClearText} minimal />}
					autoFocus
					onFocus={(e) => e.target?.select()}
					small
				/>
			) : textFilter ? (
				<Tag onClick={handleShowInput} onRemove={handleClearText} interactive minimal>
					{textFilter}
				</Tag>
			) : (
				<Button
					cy-test="search-filters-btn"
					rightIcon={'search-template'}
					onClick={handleShowInput}
					css={alignedMinimalButton}
					minimal
					small
				>
					Find
				</Button>
			)}
		</form>
	);
};

const inputStyle = css`
	flex: 1 1 auto;

	animation-duration: 200ms;
	animation-name: slideopen;
	animation-timing-function: ease-out;

	max-width: 200px;
	@keyframes slideopen {
		from {
			max-width: 70px; // approx button width
		}

		to {
			max-width: 200px;
		}
	}
`;
